
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FooterBlockConfiguration, FooterLink, SocialLink } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  BackgroundControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface FooterBlockPropertiesProps {
  config: FooterBlockConfiguration;
  onChange: (config: FooterBlockConfiguration) => void;
}

export function FooterBlockProperties({ config, onChange }: FooterBlockPropertiesProps) {
  const [newLinkText, setNewLinkText] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const updateField = <K extends keyof FooterBlockConfiguration>(
    field: K,
    value: FooterBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const addLink = () => {
    if (!newLinkText.trim()) return;
    const updated: FooterLink[] = [...(config.links ?? []), { text: newLinkText.trim(), url: newLinkUrl.trim() || '#' }];
    updateField('links', updated);
    setNewLinkText('');
    setNewLinkUrl('');
  };

  const removeLink = (index: number) => {
    updateField('links', (config.links ?? []).filter((_, i) => i !== index));
  };

  const updateLink = (index: number, field: keyof FooterLink, value: string) => {
    const updated = (config.links ?? []).map((link, i) => i === index ? { ...link, [field]: value } : link);
    updateField('links', updated);
  };

  const SOCIAL_PLATFORMS: SocialLink['platform'][] = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'pinterest', 'tiktok', 'github'];

  const addSocialLink = (platform: SocialLink['platform']) => {
    if ((config.socialLinks ?? []).some(sl => sl.platform === platform)) return;
    updateField('socialLinks', [...(config.socialLinks ?? []), { platform, url: '' }]);
  };

  const removeSocialLink = (platform: string) => {
    updateField('socialLinks', (config.socialLinks ?? []).filter(sl => sl.platform !== platform));
  };

  const updateSocialUrl = (platform: string, url: string) => {
    updateField('socialLinks', (config.socialLinks ?? []).map(sl => sl.platform === platform ? { ...sl, url } : sl));
  };

  return (
    <div className="space-y-3">
      {/* Company Info */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Company Name</Label>
        <Input
          type="text"
          value={config.companyName ?? ''}
          onChange={(e) => updateField('companyName', e.target.value || undefined)}
          placeholder="Your Company"
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Address</Label>
        <Input
          type="text"
          value={config.address ?? ''}
          onChange={(e) => updateField('address', e.target.value || undefined)}
          placeholder="123 Main St, City, Country"
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Legal Text</Label>
        <Input
          type="text"
          value={config.legalText ?? ''}
          onChange={(e) => updateField('legalText', e.target.value || undefined)}
          placeholder="© 2025 Your Company. All rights reserved."
        />
      </div>

      {/* Unsubscribe */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="show-unsubscribe"
            checked={config.showUnsubscribe}
            onChange={(e) => updateField('showUnsubscribe', e.target.checked)}
            className="w-4 h-4 rounded border-input"
          />
          <Label htmlFor="show-unsubscribe" className="text-xs font-medium cursor-pointer">Show Unsubscribe</Label>
        </div>
      </div>

      {config.showUnsubscribe && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Unsubscribe URL</Label>
          <Input
            type="url"
            value={config.unsubscribeUrl ?? ''}
            onChange={(e) => updateField('unsubscribeUrl', e.target.value || undefined)}
            placeholder="https://example.com/unsubscribe"
          />
        </div>
      )}

      <Accordion type="multiple" defaultValue={["links"]} className="w-full">

        {/* Navigation Links */}
        <AccordionItem value="links">
          <AccordionTrigger>Navigation Links</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {(config.links ?? []).map((link, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    type="text"
                    value={link.text}
                    onChange={(e) => updateLink(i, 'text', e.target.value)}
                    placeholder="Link text"
                    className="flex-1"
                  />
                  <Input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateLink(i, 'url', e.target.value)}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    className="shrink-0 rounded p-1.5 text-destructive hover:bg-destructive/10 transition-colors"
                    title="Remove link"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newLinkText}
                  onChange={(e) => setNewLinkText(e.target.value)}
                  placeholder="Link text"
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }}
                />
                <Input
                  type="url"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }}
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="shrink-0 rounded border border-border bg-background px-2 py-1.5 text-xs hover:bg-accent transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Social Links */}
        <AccordionItem value="social">
          <AccordionTrigger>Social Links</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2">
              {(config.socialLinks ?? []).map((sl) => (
                <div key={sl.platform} className="flex gap-2 items-center">
                  <span className="shrink-0 w-20 text-xs font-medium capitalize">{sl.platform}</span>
                  <Input
                    type="url"
                    value={sl.url}
                    onChange={(e) => updateSocialUrl(sl.platform, e.target.value)}
                    placeholder={`https://${sl.platform}.com/yourpage`}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeSocialLink(sl.platform)}
                    className="shrink-0 rounded p-1.5 text-destructive hover:bg-destructive/10 transition-colors"
                    title="Remove"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-1 mt-1">
                {SOCIAL_PLATFORMS
                  .filter(p => !(config.socialLinks ?? []).some(sl => sl.platform === p))
                  .map(platform => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => addSocialLink(platform)}
                      className="rounded border border-border bg-background px-2 py-1 text-xs capitalize hover:bg-accent transition-colors"
                    >
                      + {platform}
                    </button>
                  ))
                }
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Border</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
